import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "../FormField";
import { eur } from "../../helpers";
import { Plus, Trash2, Pencil, Gauge } from "lucide-react";

const TYPES = ["Luce", "Gas", "Acqua", "Internet", "Rifiuti", "Riscaldamento", "Condominio", "Altro"];
const empty = { type: "Luce", provider: "", contract_number: "", pod_pdr: "", meter_number: "", cost_monthly: "", notes: "" };

export default function UtilitiesTab({ apartmentId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/apartments/${apartmentId}/detail`).then((r) => setItems(r.data.apartment?.utilities || [])).catch(() => {});
  }, [apartmentId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...form, cost_monthly: +(form.cost_monthly || 0) };
      if (editId) await api.put(`/apartments/${apartmentId}/utilities/${editId}`, body);
      else await api.post(`/apartments/${apartmentId}/utilities`, body);
      toast.success("Salvato"); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const edit = (u) => { setEditId(u.id); setForm({ ...u, cost_monthly: u.cost_monthly || "" }); };
  const del = async (uid) => { await api.delete(`/apartments/${apartmentId}/utilities/${uid}`); load(); };

  const totMonthly = items.reduce((s, u) => s + (u.cost_monthly || 0), 0);

  return (
    <Card className="border-slate-200 p-5" data-testid="detail-utilities">
      <h3 className="mb-4 flex items-center gap-2 font-head font-semibold text-slate-900"><Gauge size={18} /> Utenze <span className="text-sm font-normal text-slate-400">({items.length} · {eur(totMonthly)}/mese)</span></h3>

      <div className="mb-4 grid grid-cols-2 gap-2 rounded-lg bg-secondary/40 p-3 sm:grid-cols-4" data-testid="utility-form">
        <Field label="Tipo">
          <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
            <SelectTrigger data-testid="util-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Fornitore"><Input data-testid="util-provider" value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} /></Field>
        <Field label="N° contratto"><Input value={form.contract_number} onChange={(e) => setForm((f) => ({ ...f, contract_number: e.target.value }))} /></Field>
        <Field label="POD/PDR"><Input value={form.pod_pdr} onChange={(e) => setForm((f) => ({ ...f, pod_pdr: e.target.value }))} /></Field>
        <Field label="N° contatore"><Input value={form.meter_number} onChange={(e) => setForm((f) => ({ ...f, meter_number: e.target.value }))} /></Field>
        <Field label="Costo €/mese"><Input type="number" value={form.cost_monthly} onChange={(e) => setForm((f) => ({ ...f, cost_monthly: e.target.value }))} /></Field>
        <Field label="Note" className="sm:col-span-2"><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
        <div className="flex items-end gap-2 sm:col-span-4">
          <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="util-save" onClick={save}><Plus size={14} className="mr-1" />{editId ? "Aggiorna" : "Aggiungi"}</Button>
          {editId && <Button size="sm" variant="outline" onClick={() => { setForm(empty); setEditId(null); }}>Annulla</Button>}
        </div>
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && <p className="text-sm text-slate-400">Nessuna utenza registrata.</p>}
        {items.map((u) => (
          <div key={u.id} data-testid={`util-${u.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
            <div><span className="font-medium text-slate-800">{u.type}</span> <span className="text-slate-500">{u.provider}</span>
              <div className="text-xs text-slate-400">{u.contract_number && `Contr. ${u.contract_number}`}{u.pod_pdr && ` · POD/PDR ${u.pod_pdr}`}{u.cost_monthly ? ` · ${eur(u.cost_monthly)}/mese` : ""}</div></div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" data-testid={`util-edit-${u.id}`} onClick={() => edit(u)}><Pencil size={13} /></Button>
              <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`util-del-${u.id}`} onClick={() => del(u.id)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
